import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2 } from "lucide-react";

/**
 * Small square book-cover thumbnail. Renders the protected file via a native
 * `<img>` with the auth token as a query param — this lets the browser cache
 * the image, lazy-load off-screen tiles, and avoids the blob->objectURL round-
 * trip that used to fire ~120 sequential fetches on the Products/Inventory
 * pages. When `onUpload` is provided, the tile is clickable and lets the user
 * pick a new image file to POST.
 */
export default function CoverThumb({ productId, coverPath, size = 44, onUpload, editable = false }) {
  const [broken, setBroken] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const src = coverPath && token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${coverPath}?auth=${encodeURIComponent(token)}`
    : "";

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !productId) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${productId}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover updated");
      setBroken(false);
      onUpload && onUpload(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const style = { width: size, height: size };
  const clickable = editable && !!productId;

  return (
    <div
      className={`relative shrink-0 rounded-sm bg-secondary overflow-hidden flex items-center justify-center ${clickable ? "cursor-pointer group" : ""}`}
      style={style}
      onClick={clickable ? () => fileRef.current?.click() : undefined}
      data-testid={productId ? `cover-thumb-${productId}` : "cover-thumb"}
    >
      {src && !broken ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
          onError={() => setBroken(true)}
        />
      ) : (
        <ImageIcon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5}/>
      )}
      {clickable && (
        <>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`cover-input-${productId}`}/>
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] uppercase tracking-widest">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Change"}
          </div>
        </>
      )}
    </div>
  );
}
